import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ConsentView, ContactPointView, NoteView } from '@today/ui-domain'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CustomerCapabilities } from '@/auth/customer-capabilities'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import {
	type AssignmentDTO,
	type ConsentDTO,
	type ContactPointDTO,
	type CustomerDTO,
	customerService,
	type NoteDTO,
} from '@/services/customer.service'
import { errorMessage, formatDate } from './customer-ui'
import type { CustomerTab } from './useCustomerDetail'

const TYPE_ORDER = ['email', 'mobile', 'landline']
type DateFormat = (iso: string | null) => string
type Act = (action: () => Promise<unknown>) => void

/** The full new id order after moving one contact up or down within its type. */
export const reorderedIds = (points: ContactPointDTO[], id: string, direction: 'up' | 'down') => {
	const target = points.find((p) => p.contactPointId === id)
	const sameType = points.filter((p) => p.type === target?.type).sort((a, b) => a.priority - b.priority)
	const ids = sameType.map((p) => p.contactPointId)
	const from = ids.indexOf(id)
	const to = direction === 'up' ? from - 1 : from + 1
	;[ids[from], ids[to]] = [ids[to], ids[from]]
	return { type: target?.type ?? 'email', ids }
}

const toPointView = (p: ContactPointDTO): ContactPointView => ({
	id: p.contactPointId,
	type: p.type,
	value: p.value,
	label: p.label,
	isPrimary: p.isPrimary,
	verified: p.verifiedAt !== null,
	invalid: p.invalidatedAt !== null,
})

const toConsentView = (c: ConsentDTO, date: DateFormat): ConsentView => ({
	id: c.consentId,
	channel: c.channel,
	purpose: c.purpose,
	status: c.status,
	legalText: c.legalTextId ? `${c.legalTextId} v${c.legalTextVersion ?? '?'}` : null,
	evidence: c.evidence
		? Object.entries(c.evidence)
				.map(([k, v]) => `${k}: ${String(v)}`)
				.join(', ')
		: null,
	evidenceRedacted: c.evidenceRedacted,
	effectiveAt: date(c.effectiveAt),
})

const toAssignmentViews = (rows: AssignmentDTO[], date: DateFormat) =>
	rows.map((a) => ({
		id: a.assignmentId,
		orgUnitName: a.orgUnit.name,
		userName: a.assignedUser.displayName,
		reason: a.reason,
		from: date(a.validFrom),
		to: a.validTo ? date(a.validTo) : null,
	}))

const toNoteViews = (rows: NoteDTO[], viewerId: string, date: DateFormat): NoteView[] =>
	rows.map((n) => ({
		id: n.noteId,
		body: n.body,
		author: n.authorDisplayName ?? '—',
		createdAt: date(n.createdAt),
		edited: n.edited,
		isOwn: n.authoredBy === viewerId,
	}))

/** Contact-point tab props; handlers only when the viewer may edit a non-anonymised customer. */
const contactPointProps = (id: string, points: ContactPointDTO[], edit: boolean, act: Act) => {
	const sorted = [...points].sort(
		(a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type) || a.priority - b.priority,
	)
	const move = (pid: string, dir: 'up' | 'down') => {
		const { type, ids } = reorderedIds(sorted, pid, dir)
		act(() => customerService.reorderContactPoints(id, type, ids))
	}
	return {
		points: sorted.map(toPointView),
		onMove: edit ? move : undefined,
		onSetPrimary: edit
			? (pid: string) => act(() => customerService.updateContactPoint(id, pid, { is_primary: true }))
			: undefined,
		onDelete: edit ? (pid: string) => act(() => customerService.deleteContactPoint(id, pid)) : undefined,
		onAdd: edit
			? (input: { type: string; value: string }) => act(() => customerService.addContactPoint(id, input))
			: undefined,
	}
}

const noteHandlers = (id: string, edit: boolean, act: Act) => ({
	onAdd: edit ? (body: string) => act(() => customerService.addNote(id, body)) : undefined,
	onEdit: edit ? (nid: string, body: string) => act(() => customerService.updateNote(id, nid, body)) : undefined,
	onDelete: edit ? (nid: string) => act(() => customerService.deleteNote(id, nid)) : undefined,
})

/** One tab's data, fetched only while that tab is selected and the viewer may see it. */
function useTabQuery<T>(id: string, tab: CustomerTab, name: CustomerTab, fn: () => Promise<T>, allowed = true) {
	return useQuery({ queryKey: ['customer-tab', id, name], queryFn: fn, enabled: tab === name && allowed })
}

/** A tab mutation: refresh the tabs and the customer on success, or a translated toast on failure. */
function useTabAction(id: string) {
	const { t, i18n } = useTranslation()
	const client = useQueryClient()
	const [toast, setToast] = useState<string | null>(null)
	const run = useMutation({
		mutationFn: (action: () => Promise<unknown>) => action(),
		onSuccess: async () => {
			setToast(null)
			await client.invalidateQueries({ queryKey: ['customer-tab', id] })
			await client.invalidateQueries({ queryKey: ['customer', id] })
		},
		onError: (error) => setToast(errorMessage(error, t, (k) => i18n.exists(k))),
	})
	return { toast, act: (action: () => Promise<unknown>) => run.mutate(action) }
}

/** Data + handlers of the detail tabs (TO-418 customer-ui REQ-006/REQ-008); each tab loads when selected. */
export function useCustomerTabs(id: string, tab: CustomerTab, caps: CustomerCapabilities, customer?: CustomerDTO) {
	const { t, i18n } = useTranslation()
	const viewerId = useViewerPermissions().userId
	const { toast, act } = useTabAction(id)
	const points = useTabQuery(id, tab, 'contactPoints', () => customerService.contactPoints(id))
	const assignments = useTabQuery(id, tab, 'assignment', () => customerService.assignments(id))
	const notes = useTabQuery(id, tab, 'notes', () => customerService.notes(id), caps.piiRead)
	const consents = useTabQuery(id, tab, 'consents', () => customerService.consents(id))
	const attributes = useTabQuery(id, tab, 'attributes', () => customerService.attributes(id), caps.piiRead)
	const activity = useTabQuery(id, tab, 'activity', () => customerService.activity(id), caps.activity)
	const mutable = !!customer && !customer.anonymizedAt
	const date: DateFormat = (iso) => formatDate(iso, i18n.language)
	const record = (input: { channel: string; purpose: string; status: string }) =>
		act(() => customerService.recordConsent(id, input))
	return {
		toast,
		contactPoints: { t, ...contactPointProps(id, points.data ?? [], mutable && caps.write, act) },
		assignments: toAssignmentViews(assignments.data ?? [], date),
		notes: { t, notes: toNoteViews(notes.data ?? [], viewerId, date), ...noteHandlers(id, mutable && caps.write, act) },
		consents: {
			t,
			current: (consents.data?.current ?? []).map((c) => toConsentView(c, date)),
			history: (consents.data?.history ?? []).map((c) => toConsentView(c, date)),
			onRecord: mutable && caps.recordConsent ? record : undefined,
		},
		attributes: (attributes.data ?? []).map((a) => ({ key: a.key, value: JSON.stringify(a.value), source: a.source })),
		activity: (activity.data ?? []).map((e) => ({ id: e.eventId, label: e.type, when: date(e.timestamp) })),
	}
}
